import React, { useState, useEffect } from 'react';
import MultiResultRow from './MultiResultRow';
import CircularProgress from '@mui/material/CircularProgress';
import { exportToCSV } from '../../Common/CSVManage';
import { Typography } from '@mui/material';
import {
  TicketButton,
  NextButton,
  StyledInput,
} from '../../Common/MUIStyledComponents';
import { Modal } from '@mui/material';
import RecordLenses from './RecordLenses';
import WExpandedMultiRecord from './WExpandedMultiRecord';

interface IMultiScreeningProps {
  results: any;
  page: number;
  setPage: any;
  inputs: any;
  handleSubmit: any;
  type: string;
  setShowFullRecord: any;
  cyberHeaders: string[];
  handleMultiPageChange?: any;
  createTicket?: any;
  saving?: boolean;
  setFrequency?: any;
  frequency?: string;
  shareEmail: string[];
  setShareEmail?: any;
  ticketNameOpen: boolean;
  setTicketNameOpen: any;
  newTicketName: string;
  setNewTicketName: any;
  handleOpenModal?: any;
  autoOpenFirstLens?: boolean;
  defaultResultLevel?: 2 | 3;
}

const WorkflowMulti: React.FC<IMultiScreeningProps> = ({
  setShowFullRecord,
  type,
  handleSubmit,
  setPage,
  results,
  cyberHeaders,
  autoOpenFirstLens,
  defaultResultLevel = autoOpenFirstLens ? 3 : 2,
}) => {
  const [totalResults, setTotalResults] = useState<any>({
    isLoading: true,
    data: 0,
  });
  const resultsPerPage = 50;
  const [, setPageCount] = useState<number>(0);
  const [expandedMulti, setExpandedMulti] = useState(false);
  const [selectedResult, setSelectedResult] = useState(false);
  const [selectedLens, setSelectedLens] = useState('');
  const [expandedRow, setExpandedRow] = useState<any>({});
  const [filteredExpandedRow, setFilteredExpandedRow] = useState<any>({});
  const [rowIndex, setRowIndex] = useState<number>(0);
  const [fileName, setFileName] = useState<string>('');
  const [fileNameOpen, setFileNameOpen] = useState(false);

  const entHeaders = [
    'Name of Entity',
    'Entity Country',
    'List Name',
    'Agency',
    'Additional Remarks',
    'Date Updated',
  ];
  const comHeaders = [
    'Product Name',
    'HS Code',
    'Source Country',
    'Agency',
    'List Name',
    'Date Updated',
  ];
  const cveHeaders = [
    'CVE ID',
    'Vendor Project',
    'Product',
    'Vulnerability Name',
    'Required Action',
  ];
  const markHeaders = [
    'Market Name',
    'Description',
    'Website',
    'Market Location',
  ];
  const selectedResultName =
    expandedRow?.body?.hits?.hits?.[0]?.matched_queries ||
    results?.data?.[rowIndex]?.result?.primary ||
    results?.data?.[rowIndex]?.primary ||
    results?.data?.[rowIndex]?.result?.query ||
    results?.data?.[rowIndex]?.query ||
    'Screening Results';

  const handleCSV = () => {
    if (results?.data[rowIndex]?.result?.body?.hits?.hits.length >= 1) {
      exportToCSV(
        fileName,
        [...results?.data[rowIndex]?.result?.body?.hits?.hits],
        type === 'Entities'
          ? entHeaders
          : type === 'Products'
          ? comHeaders
          : cyberHeaders.includes('CVE ID')
          ? cveHeaders
          : markHeaders,
        type === 'Entities'
          ? 'entity'
          : type === 'Products'
          ? 'commodity'
          : cyberHeaders.includes('CVE ID')
          ? 'CVE'
          : 'Markets',
      );
    }
  };

  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameOpen(false);
    setFileName('');
  };

  const getTotalResults = (resultsArr: any) => {
    let total = 0;
    resultsArr?.map(
      (row: any) => (total = total + row?.result?.body?.hits?.total?.value),
    );
    setTotalResults({ isLoading: false, data: total });
  };

  useEffect(() => {
    const totalPages = Math.ceil((totalResults.data || 0) / resultsPerPage);
    if (totalPages >= 200) {
      setPageCount(199);
    } else if (totalPages <= 199) {
      setPageCount(totalPages);
    }
    if (expandedMulti) {
      setExpandedRow(results.data[rowIndex].result);
    }
    const shouldOpenFirstLens =
      defaultResultLevel === 3 || Boolean(autoOpenFirstLens);

    if (shouldOpenFirstLens && results.data.length === 1) {
      const firstResult = results.data[0].result;
      const firstLens =
        firstResult?.body?.aggregations?.lensAgg?.buckets?.[0]?.key ||
        firstResult?.body?.hits?.hits?.[0]?._source?.['BITE Lens'];

      setExpandedRow(firstResult);
      setRowIndex(0);
      setSelectedResult(true);

      if (firstLens) {
        // structuredClone isn't available on every supported runtime (older
        // Safari, embedded webviews). Fall back to the JSON deep-clone so this
        // auto-open path can't crash before the screening results render.
        const clonedFirstResult =
          typeof structuredClone === 'function'
            ? structuredClone(firstResult)
            : JSON.parse(JSON.stringify(firstResult));
        const filteredRow = filterByBiteLens(clonedFirstResult, firstLens);
        setFilteredExpandedRow(filteredRow);
        setSelectedLens(firstLens);
        setExpandedMulti(true);
      }

      return;
    }
    if (results.data.length === 1) {
      setExpandedRow(results.data[0].result);
      setFilteredExpandedRow({});
      setRowIndex(0);
      setSelectedResult(true);
      setSelectedLens('');
      setExpandedMulti(false);
    }
  }, [results, autoOpenFirstLens, defaultResultLevel]);

  useEffect(() => {
    getTotalResults(results.data);
  }, [results.data]);

  const handleExpandRow = (row: any, i: number) => {
    setExpandedRow(row);
    setRowIndex(i);
    setSelectedResult(true);
  };

  const filterByBiteLens = (obj: any, value: string) => {
    obj.body.hits.hits = obj.body.hits.hits.filter(
      (hit: any) => hit._source['BITE Lens'] === value,
    );

    return obj;
  };

  const handleSelectLens = (lens: string) => {
    const arr =
      typeof structuredClone === 'function'
        ? structuredClone(expandedRow)
        : JSON.parse(JSON.stringify(expandedRow));
    const newObj = filterByBiteLens(arr, lens);
    setFilteredExpandedRow(newObj);
    setSelectedLens(lens);
    setExpandedMulti(true);
  };

  return (
    <div className='screening-results-container'>
      <div className='screening-results-header'>
        <div className='screening-results-header-group'>
          <div className='screening-results-header-frame'>
            {!expandedMulti && !selectedResult ? (
              <div className='screening-results-matches'>
                <div className='screening-results-matches-inner'>
                  {!totalResults.isLoading && (
                    <div className='screening-matches-text'>
                      {totalResults.data ? totalResults.data : 0} Results
                    </div>
                  )}
                </div>
              </div>
            ) : !expandedMulti && selectedResult ? (
              <div className='screening-results-expanded-name-container'>
                <div className='screening-results-expanded-name-frame'>
                  <svg
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedResult(false);
                      setPage(0);
                    }}
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M11.25 4.5L6.75 9L11.25 13.5'
                      stroke='#D0D5DD'
                      strokeWidth='1.33333'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <div className='screening-results-expanded-name-text'>
                    {selectedResultName}
                  </div>
                  <div className='screening-results-matches'>
                    <div className='screening-results-matches-inner'>
                      {!totalResults.isLoading && (
                        <div className='screening-matches-text'>
                          {totalResults.data ? totalResults.data : 0} Results
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className='screening-results-expanded-name-container'>
                <div className='screening-results-expanded-name-frame'>
                  <svg
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setExpandedMulti(false);
                      setPage(0);
                    }}
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M11.25 4.5L6.75 9L11.25 13.5'
                      stroke='#D0D5DD'
                      strokeWidth='1.33333'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <div className='screening-results-expanded-name-text'>
                    {selectedLens}
                  </div>
                  <div className='screening-results-matches'>
                    <div className='screening-results-matches-inner'>
                      {!totalResults.isLoading && (
                        <div className='screening-matches-text'>
                          {filteredExpandedRow?.body?.hits?.hits?.length
                            ? filteredExpandedRow?.body?.hits?.hits?.length
                            : 0}{' '}
                          Results
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {!expandedMulti && !selectedResult && (
        <div className='screening-results-columns'>
          <div className='screening-table-title-column'>
            <div className='screening-table-title-column-inner'>
              <div className='screening-table-column-text'>Name of Entity</div>
            </div>
          </div>
          <div className='screening-table-title-column'>
            <div className='screening-table-title-column-inner'>
              <div className='screening-table-column-text'>Results</div>
            </div>
          </div>
          <div className='screening-table-placeholder-column'></div>
        </div>
      )}
      {!results.isLoading && results.data ? (
        !expandedMulti && !selectedResult ? (
          <div className='screening-results-table-container'>
            {results?.data?.map((row: any, index: number) => (
              <MultiResultRow
                handleExpandedRow={handleExpandRow}
                row={row}
                index={index}
                key={index}
              />
            ))}
          </div>
        ) : selectedResult && !expandedMulti ? (
          <RecordLenses row={expandedRow} handleSelectLens={handleSelectLens} />
        ) : (
          expandedMulti && (
            <WExpandedMultiRecord
              showFilters={false}
              setShowFullRecord={setShowFullRecord}
              type={type}
              handleSubmit={handleSubmit}
              row={filteredExpandedRow}
              cyberHeaders={cyberHeaders}
            />
          )
        )
      ) : (
        <div>
          <CircularProgress style={{ color: '#0E8BFF' }} />
        </div>
      )}
      <Modal open={fileNameOpen} onClose={handleFileNameClose}>
        <div className='bite-score-file-modal'>
          <div className='display-flex justify-between width-100'>
            <Typography variant='h5'>File Name:</Typography>
            <TicketButton
              onClick={handleFileNameClose}
              className='cursor-pointer'
            />
          </div>
          <div className='display-flex justify-end width-100'>
            <StyledInput
              value={fileName}
              onChange={handleFileNameChange}
              variant='outlined'
              className='margin-right-20px'
            />
            <NextButton
              variant='contained'
              color='primary'
              onClick={() => {
                handleCSV();
                handleFileNameClose();
              }}
            >
              Download
            </NextButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WorkflowMulti;
